// A MassPay talent's wallet lives on MassPay's member site, where they
// withdraw to GoPay / OVO / DANA / a bank (MassPay, 2026-10-14).
import { Capacitor } from "@capacitor/core";
import { openExternal } from "./openExternal";
import { masspayApi } from "../services/masspay";

export const MASSPAY_WALLET_URL = "https://members.masspay.io/v2/login";
/** How long "Open my MassPay wallet" waits for our server before it opens the login page anyway. */
export const WALLET_LINK_WAIT_MS = 2500;

/** MassPay's own pages: the in-app browser on the phone, a new tab on the
 *  website (so Request App stays open behind it). */
export function openMasspayPage(url: string = MASSPAY_WALLET_URL): void {
  if (Capacitor.isNativePlatform()) void openExternal(url);
  else window.open(url, "_blank", "noopener");
}

function isMasspayPage(url: unknown): url is string {
  if (typeof url !== "string") return false;
  try {
    const u = new URL(url);
    const host = u.hostname.toLowerCase();
    return u.protocol === "https:" && (host === "masspay.io" || host.endsWith(".masspay.io"));
  } catch {
    return false;
  }
}

/** Where the talent's wallet goes: MassPay's activation page while their
 *  member account has no password yet (MassPay's "Activate now" email only
 *  comes with the first payment), else the login page. Any error, or no
 *  answer in time → the login page, exactly as before. */
export async function masspayWalletUrl(waitMs: number = WALLET_LINK_WAIT_MS): Promise<string> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const tooLate = new Promise<null>((resolve) => {
    timer = setTimeout(() => resolve(null), waitMs);
  });
  try {
    const answer = await Promise.race([masspayApi.walletLink(waitMs), tooLate]);
    return answer && isMasspayPage(answer.url) ? answer.url : MASSPAY_WALLET_URL;
  } catch {
    return MASSPAY_WALLET_URL;
  } finally {
    clearTimeout(timer);
  }
}

/** "Open my MassPay wallet" (the Balance card's MassPay chip, "Open MassPay"
 *  after a cash-out). On the website the tab is opened inside the tap — a tab
 *  opened after a network wait is blocked as a popup — and pointed at the
 *  link once it's known, as for the ID check (lib/idCheckFlow). */
export async function openMasspayWallet(): Promise<void> {
  const native = Capacitor.isNativePlatform();
  let tab: Window | null = null;
  if (!native) {
    try {
      tab = window.open("", "_blank");
      // MassPay's pages must not be able to reach back into the app.
      if (tab) tab.opener = null;
    } catch {
      tab = null;
    }
  }
  const url = await masspayWalletUrl();
  if (native) void openExternal(url);
  else if (tab && !tab.closed) tab.location.href = url;
  else window.open(url, "_blank", "noopener");
}
